/**
 * @copyright (C), Copyright 2021. COCONE CORPORATION. All rights Reserved.
 * @author 崔祥勳: ' choi_sanghoon@cocone.co.jp ',
 */

import { throttle } from 'lodash';
import qs from 'qs';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useEffectOnce, useTitle } from 'react-use';
import { AutoSizer, Grid, GridCellProps, ScrollParams, WindowScroller } from 'react-virtualized';
import ScreenInfo, { MOBILE_HORIZONTAL_PADDING, MOBILE_WIDTH } from 'src/context/screen_constants';
import { useAuth } from 'src/hooks/useAuth';
import { useLocalSettings } from 'src/hooks/useLocalSettings';
import { useRpc } from 'src/hooks/useRpc';
import { RPC_ASSET_CREATOR_COLLECTION_VIEW } from 'src/model/rpcType';
import { CbtCreationStatus_e, CbtSchemaCardInfo_i } from 'src/model/rpcModel';
import R from 'src/res/R';
import { messageService, CbtStompMsgCode_e } from 'src/rx/messageService';
import { devOutlineF, FlexColumnDiv, FlexRowDiv, HEIGHT, WIDTH } from 'src/ui/base_component/etc';
import MobileScalableFlexColumn from 'src/ui/base_component/MobileScalableFlexColumn';
import TextCon from 'src/ui/base_component/TextCon';
import TextConMulti from 'src/ui/base_component/TextConMulti';
import { BackBtn } from 'src/ui/common_component/button/BackBtn';
import { RoundTxtBtn } from 'src/ui/common_component/button/RoundTxtBtn';
import { SAddBtn } from 'src/ui/common_component/button/SAddBtn';
import { CollectionCard } from 'src/ui/common_component/CollectionCard';
import { CommonFooter, COMMON_FOONTER_HEIGHT } from 'src/ui/common_component/CommonFooter';
import { SchemaCardForList } from 'src/ui/common_component/SchemaCardForList';
import { SchemaCardNew } from 'src/ui/common_component/SchemaCardNew';
import { Title } from 'src/ui/common_component/Title';
import { UserThumbName } from 'src/ui/common_component/UserThumbName';
import { VLabelValue } from 'src/ui/common_component/VLabelValue';
import { CLAY, CommonColumnBox, CommonRowBox, COMMON_CARD_COLUMN_CNT, SCHEMA_CARD_LAYOUT } from 'src/ui/layout_constant';
import { Nav } from 'src/ui/screen/Nav';
import { DBGMSG, DBGMSGW, Utils } from 'src/util/utils';
import { useDlgFactory } from 'src/hooks/useDlgFactory';
import { DataFetching } from 'src/ui/common_component/DataFetching';
import { AppConf, isProd } from 'src/conf/AppConf';
import { useCheckUserCertTpAndBlock } from 'src/hooks/useCheckUserCertTpAndBlock';
import { UserDetailMenuType_e } from 'src/redux/modules/userDetailModule';

const devOutline = {
  ...devOutlineF,
  borderColor: undefined,
  borderWidth: 0,
};

/**************************************
 * !! type
 **************************************/
enum SchemaCardType_e {
  NewSchemaCard,
}

/**************************************
 * !! constant
 **************************************/
const DT_TITLE_MARGIN_BOTTOM = 14;
const DT_INFOBOX_MARGIN_TOP = 30;
const DT_INFOBOX_WIDTH = 888;
const DT_INFOBOX_HEIGHT = 888;
const DT_COLUMN_WIDTH = 888 / 2 - 1;
const DT_MARGIN_BOTTOM = 120;

const MB_TITLE_MARGIN_BOTTOM = 8;
const MB_INFOBOX_MARGIN_TOP = 20;
const MB_INFOBOX_WIDTH = ScreenInfo.contentsWidth;
const MB_INFOBOX_HEIGHT = ScreenInfo.contentsWidth;
const MB_COLUMN_WIDTH = ScreenInfo.contentsWidth;
const MB_MARGIN_BOTTOM = 60;

export const MY_COLLECTION_R_TITLE_MARGIN_BOTTOM = ScreenInfo.isMobile() ? MB_TITLE_MARGIN_BOTTOM : DT_TITLE_MARGIN_BOTTOM;
export const MY_COLLECTION_R_INFOBOX_MARGIN_TOP = ScreenInfo.isMobile() ? MB_INFOBOX_MARGIN_TOP : DT_INFOBOX_MARGIN_TOP;
export const MY_COLLECTION_R_INFOBOX_WIDTH = ScreenInfo.isMobile() ? MB_INFOBOX_WIDTH : DT_INFOBOX_WIDTH;
export const INFOBOX_HEIGHT = ScreenInfo.isMobile() ? MB_INFOBOX_HEIGHT : DT_INFOBOX_HEIGHT;
export const MY_COLLECTION_R_COLUMN_WIDTH = ScreenInfo.isMobile() ? MB_COLUMN_WIDTH : DT_COLUMN_WIDTH;
export const MARGIN_BOTTOM = ScreenInfo.isMobile() ? MB_MARGIN_BOTTOM : DT_MARGIN_BOTTOM;

/**************************************
 * !! styled component
 **************************************/

type Props = {};

export function MyCollectionRScreen(props: Props) {
  /**************************************
   * !! state
   **************************************/
  const [sFetchedData, setFetchedData] = useState<RPC_ASSET_CREATOR_COLLECTION_VIEW.Rx>();
  const [sSchemaList, setSchemaList] = useState<(CbtSchemaCardInfo_i | SchemaCardType_e)[]>([]);

  const [sNextPageStatus, setNextPageStatus] = useState<string>('');

  const [sHeightTest, setHeightTest] = useState<number>();

  /**************************************
   * !! ref
   **************************************/
  const fetchedDataRef = useRef<RPC_ASSET_CREATOR_COLLECTION_VIEW.Rx>();
  const nextPageLoadingRef = useRef(false);
  const hasMorePageRef = useRef<boolean>(false);
  const qsRef = useRef<{ cid: number }>();

  /**************************************
   * !! hooks
   **************************************/
  useTitle(`AssetCreator - ${AppConf.APPNAME} ${isProd() ? '' : AppConf.PHASE}`);
  const location = useLocation();
  const hHistory = useHistory();
  const hAuth = useAuth();
  const hR = useLocalSettings();
  const hRpc = useRpc();
  const hDlgFactory = useDlgFactory();
  // useCheckUserCertTpAndBlock();

  /**************************************
   * !! animation
   **************************************/

  /**************************************
   * !! useEffect
   **************************************/
  useEffect(() => {
    DBGMSG('useEffect() init');

    // query string parsing
    const query: { cid?: string } = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });

    if (query.cid) {
      DBGMSG(`$$$$QS id: ${query.cid}`);
      const cid = parseInt(query.cid);
      qsRef.current = { cid };
      fetchData(cid);
    } else {
      // key 없이 접근
      return;
    }

    return () => {
      DBGMSG('useEffect() release');
    };
  }, []);

  useEffect(() => {
    const subscription = messageService.onMessage().subscribe((message) => {
      if (!qsRef.current) return;

      switch (message.kind) {
        case 'ReceiveStompMsg':
          if (
            message.code === CbtStompMsgCode_e.ASSET_SCHEMA_CREATE_FAIL ||
            message.code === CbtStompMsgCode_e.ASSET_SCHEMA_CREATE_SUCCESS ||
            message.code === CbtStompMsgCode_e.ASSET_SCHEMA_EDIT_FAIL ||
            message.code === CbtStompMsgCode_e.ASSET_SCHEMA_EDIT_SUCCESS
          ) {
            fetchData(qsRef.current.cid);
          }
          // code(행동)은 각 screen이 구독하여 결정
          break;
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffectOnce(() => {});

  /**************************************
   * !! arrow function
   **************************************/
  const fetchData = async (collectionId: number) => {
    const res = await hRpc.cobaltRpc.rpcAssetCreatorCollectionView({ cid: collectionId });
    setFetchedData(res);
    fetchedDataRef.current = res;

    if (res.schm_list.length === 0) {
      setSchemaList([]);
    } else {
      setSchemaList([SchemaCardType_e.NewSchemaCard, ...res.schm_list]);
    }
  };

  const getLastRowId = () => {
    if (!fetchedDataRef.current) return;
    if (!fetchedDataRef.current.pg_info) return;
    return fetchedDataRef.current.pg_info.last_row_id;
  };

  const hasMorePage = () => {
    const lastRowId = getLastRowId();
    if (!lastRowId) {
      DBGMSGW('lastRowId is undefined');
      return false;
    }

    return lastRowId !== -1;
  };

  const isLastPage = () => {
    const lastRowId = getLastRowId();
    if (lastRowId === undefined) {
      DBGMSGW('lastRowId is undefined');
      return false;
    }

    return lastRowId === -1;
  };

  // 다음페이지 요청
  const fetchItemListMore = async () => {
    if (!fetchedDataRef.current) {
      DBGMSG('$$$$PAGE_SCROLL', 'sFetcheAstListDataRef is undefined');
      return Promise.reject();
    }

    if (sSchemaList.length === 0) {
      DBGMSG('$$$$PAGE_SCROLL', 'sCollectionList.length === 0');
      return Promise.reject();
    }

    if (!qsRef.current) {
      DBGMSG('$$$$PAGE_SCROLL', 'qsRef.current is undefined');
      return;
    }

    nextPageLoadingRef.current = true;

    const pg_info = fetchedDataRef.current.pg_info;

    try {
      setNextPageStatus('loading');
      const res = await hRpc.cobaltRpc.rpcAssetCreatorCollectionView({ cid: qsRef.current.cid, pg_info });
      setFetchedData(res);
      setSchemaList([...sSchemaList, ...res.schm_list]);
      setNextPageStatus('');
      nextPageLoadingRef.current = false;
      fetchedDataRef.current = res;
    } catch (err) {
      setNextPageStatus('loading fail');
      nextPageLoadingRef.current = false;
    }
  };

  // 스크롤 리스너
  const scrollListener = (e: ScrollParams) => {
    // DBGMSG('$$$$PAGE_SCROLL');
    // clientHeight 화면 높이
    // scrollHeight 스크롤 높이
    // scrollTop 현재 스크롤 위치
    if (!('clientHeight' in e && 'scrollHeight' in e && 'scrollTop' in e)) {
      // DBGMSG(`FATAL scrollListener`);
      DBGMSG('$$$$PAGE_SCROLL');
      return;
    }

    if (!fetchedDataRef.current) {
      DBGMSG('$$$$PAGE_SCROLL', 'sFetcheAstListDataRef is undefined');
      return;
    }

    const { clientHeight, scrollHeight, scrollTop } = e;

    const atBottom = scrollTop + clientHeight >= scrollHeight - COMMON_FOONTER_HEIGHT;
    if (!atBottom) return;
    // DBGMSG('$$$$PAGE_SCROLL', `clientHeight: ${clientHeight}, scrollHeight: ${scrollHeight}, scrollTop: ${scrollTop}`);
    // DBGMSG('$$$$PAGE_SCROLL', `${scrollTop + clientHeight} >= ${scrollHeight - COMMON_FOONTER_HEIGHT}`);
    // DBGMSG('$$$$PAGE_SCROLL', `atBottom: ${atBottom} `);

    // 다음 페이지 요청중일때 중복요청 return
    if (nextPageLoadingRef.current) {
      DBGMSG('$$$$PAGE_SCROLL', `return triggeredRef.current is true `);
      return;
    }

    // 다음페이지가 없을경우 return
    if (isLastPage() === true) {
      DBGMSG('$$$$PAGE_SCROLL', `: no more page`);
      return;
    }

    if (atBottom) {
      fetchItemListMore()
        .then(() => {
          DBGMSG('$$$$PAGE_SCROLL', 'fetchAstListDataNextPage then');
        })
        .catch(() => {
          DBGMSG('$$$$PAGE_SCROLL', 'fetchAstListDataNextPage catch');
        });
    }
  };

  // 스크롤 리스너 throttle
  const throttleScrollListener = throttle(scrollListener, 100);

  if (!sFetchedData) return <DataFetching />;

  /**************************************
   * !! render function
   **************************************/
  // 아이템 렌더링
  const cellRenderer = ({
    key, // Unique key within array of rows
    columnIndex, // Index of row within collection
    rowIndex, // Index of row within collection
    isScrolling, // The List is currently being scrolled
    isVisible, // This row is visible within the List (eg it is not an overscanned row)
    style, // Style object to be applied to row (to position it)
  }: GridCellProps) => {
    let index: number;
    let item: CbtSchemaCardInfo_i | SchemaCardType_e;
    index = rowIndex * COMMON_CARD_COLUMN_CNT + columnIndex;
    item = sSchemaList[index];

    if (index >= sSchemaList.length) return null;

    // DBGMSG(`typeof item: ${typeof item}`);
    return (
      // 중요!
      <div key={key} style={style}>
        {item === SchemaCardType_e.NewSchemaCard ? (
          <MobileScalableFlexColumn>
            <SchemaCardNew
              isAnim
              onClick={() => {
                if (!sFetchedData) return;
                if (sFetchedData.creatable_schm) hHistory.push(Nav.brewSchemaCScreenPath({ cid: sFetchedData.col_info.cid }));
                else {
                  hDlgFactory.showSimpleOk({ text: hR.strings.DLG_EXCEED_SCHEMA_LIMIT });
                }
              }}
            />
          </MobileScalableFlexColumn>
        ) : (
          <MobileScalableFlexColumn>
            <SchemaCardForList
              schemaInfo={item}
              isAnim
              onClick={() => {
                if ((item as CbtSchemaCardInfo_i).status === CbtCreationStatus_e.InPreparation) {
                  DBGMSGW('생성중입니다.');
                } else {
                  const id = (item as CbtSchemaCardInfo_i).sid;
                  hHistory.push(Nav.brewSchemaRScreenPath({ sid: id }));
                }
              }}
            />
          </MobileScalableFlexColumn>
        )}
      </div>
    );
  };

  // 스키마 목록 렌더링
  const renderList = () => {
    DBGMSG('renderList');
    return (
      <FlexColumnDiv
        style={{
          width: SCHEMA_CARD_LAYOUT.widthForList * ScreenInfo.scale * COMMON_CARD_COLUMN_CNT,
          alignSelf: 'center',
          userSelect: 'none',
          // ...devOutlineF,
        }}
      >
        <WindowScroller scrollElement={window}>
          {({ height, isScrolling, registerChild, onChildScroll, scrollTop }) => (
            <FlexColumnDiv>
              <AutoSizer disableHeight>
                {({ width }) => (
                  <div ref={registerChild}>
                    <Grid
                      onScroll={(params) => {
                        onChildScroll(params);
                        throttleScrollListener(params);
                      }}
                      isScrolling={isScrolling}
                      autoHeight
                      cellRenderer={cellRenderer}
                      columnWidth={SCHEMA_CARD_LAYOUT.widthForList * ScreenInfo.scale}
                      columnCount={COMMON_CARD_COLUMN_CNT}
                      height={height}
                      rowHeight={SCHEMA_CARD_LAYOUT.heightForList * ScreenInfo.scale}
                      rowCount={Math.ceil(sSchemaList.length / COMMON_CARD_COLUMN_CNT)}
                      scrollTop={scrollTop}
                      width={SCHEMA_CARD_LAYOUT.widthForList * ScreenInfo.scale * COMMON_CARD_COLUMN_CNT}
                    />
                  </div>
                )}
              </AutoSizer>
            </FlexColumnDiv>
          )}
        </WindowScroller>
        {/* 다음페이지 로딩표시 */}
        <FlexRowDiv
          style={{
            height: 40,
            willChange: 'scroll-position',
            justifyContent: 'center',
            alignItems: 'center',
            // ...devOutlineF,
          }}
        >
          {sNextPageStatus === 'loading' && <DataFetching containerWidth={40} containerHeight={40} width={40} height={40} />}
        </FlexRowDiv>
      </FlexColumnDiv>
    );
  };

  const renderEditBtn = () => {
    return (
      <RoundTxtBtn
        enable
        width={CLAY.TEXT_BTN_SIZE.WIDTH}
        height={CLAY.TEXT_BTN_SIZE.HEIGHT}
        text={hR.strings.AC_COLLECTION_READ_EDIT_BTN}
        fontSize={CLAY.TEXT_BTN_FONT_SIZE}
        onClick={() => {
          DBGMSG('edit');
          hHistory.push(Nav.brewCollectionUScreenPath({ cid: sFetchedData.col_info.cid }));
        }}
      />
    );
  };

  const renderCollectionInfo = () => {
    return (
      <FlexColumnDiv>
        {/* collection 이름 */}
        <Title title={`${hR.strings.AC_COLLECTION_READ_TITLE} - ${sFetchedData.col_info.dp_name}`} />

        <HEIGHT size={MY_COLLECTION_R_TITLE_MARGIN_BOTTOM} />

        {/* collection 소유자 정보 */}
        <UserThumbName
          thumb={null}
          kind={'OWNER'}
          userId={sFetchedData.col_info.creator.ucode}
          onClick={() => {
            hHistory.push(Nav.brewUserDetailScreenPath({ ucode: sFetchedData.col_info.creator.ucode, menu: UserDetailMenuType_e.INVEN }));
          }}
        />
      </FlexColumnDiv>
    );
  };

  const renderCollectionInfoBox = () => {
    return (
      <CommonRowBox
        style={{
          width: MY_COLLECTION_R_INFOBOX_WIDTH,
          flexWrap: 'wrap',
          paddingTop: CLAY.BOX_PADDING,
          paddingBottom: CLAY.BOX_PADDING,
          // ...devOutlineF,
        }}
      >
        <FlexColumnDiv
          style={{
            width: MY_COLLECTION_R_COLUMN_WIDTH,
            paddingLeft: CLAY.BOX_PADDING,
            paddingRight: CLAY.BOX_PADDING,
            //
            // ...devOutlineF,
          }}
        >
          {/* 컬렉션 이름 */}
          <VLabelValue title={hR.strings.AC_COLLECTION_READ_COLLECTION_NAME} value={sFetchedData.col_info.name} />

          <HEIGHT size={CLAY.FORM_BETWEEN_SPACE} />

          {/* 디스플레이 이름 */}
          <VLabelValue title={hR.strings.AC_COLLECTION_READ_DISP_NAME} value={sFetchedData.col_info.dp_name} />

          <HEIGHT size={CLAY.FORM_BETWEEN_SPACE} />

          {/* 인세 */}
          <VLabelValue title={hR.strings.AC_COLLECTION_READ_ROYALTY} value={`${sFetchedData.col_info.royalty}%`} />

          <HEIGHT size={CLAY.FORM_BETWEEN_SPACE} />

          {/* 웹 사이트 URL */}
          <VLabelValue
            title={hR.strings.AC_COLLECTION_READ_WEBSITE}
            value={sFetchedData.col_info.svc_url ?? ''}
            valueColor={R.colors.purpleishBlue}
            valueOnClick={(link) => {
              if (sFetchedData.col_info.svc_url === null) return;
              if (link.search('http://') === -1 && link.search('https://') === -1) {
                window.open(`http://${link}`);
              } else {
                window.open(link);
              }
            }}
          />
          <HEIGHT size={CLAY.FORM_BETWEEN_SPACE} />
        </FlexColumnDiv>

        <FlexColumnDiv
          style={{
            width: MY_COLLECTION_R_COLUMN_WIDTH,
            paddingLeft: CLAY.BOX_PADDING,
            paddingRight: CLAY.BOX_PADDING,
          }}
        >
          {/* 설명 */}
          <VLabelValue title={hR.strings.AC_COLLECTION_READ_DESC} value={sFetchedData.col_info.desc ?? ''} />

          {/* 관리자 계정 */}
          {sFetchedData.col_info.admin_list.length > 0 && (
            <>
              <HEIGHT size={CLAY.FORM_BETWEEN_SPACE} />
              <VLabelValue
                title={hR.strings.AC_COLLECTION_READ_ADMIN}
                value={sFetchedData.col_info.admin_list}
                valueColor={R.colors.purpleishBlue}
                valueOnClick={(v) => {
                  DBGMSG(v);
                }}
              />
            </>
          )}
        </FlexColumnDiv>
      </CommonRowBox>
    );
  };

  /**************************************
   * !! render conf
   **************************************/

  if (ScreenInfo.isDesktop() || ScreenInfo.isTablet()) {
    return (
      <FlexColumnDiv
        style={{
          backgroundColor: R.colors.bgGrey,
          width: ScreenInfo.contentsWidth,
          alignItems: 'flex-start',
          paddingTop: CLAY.TITLE_PADDING_TOP,
          ...devOutline,
        }}
      >
        {/* Back 버튼 */}
        <BackBtn />

        <HEIGHT size={30} />

        <FlexRowDiv
          style={{
            alignSelf: 'stretch',
          }}
        >
          <FlexColumnDiv>
            {/* Collection 카드 */}
            <CollectionCard collectionInfo={sFetchedData.col_info} />

            <HEIGHT size={30} />

            {/* 편집하기 버튼 */}
            {renderEditBtn()}
          </FlexColumnDiv>

          <WIDTH size={30} />

          {/* collection 정보 */}
          <FlexColumnDiv style={{ flex: 1, ...devOutline }}>
            {renderCollectionInfo()}

            <HEIGHT size={30} />

            {/* collection 박스 */}
            {renderCollectionInfoBox()}
          </FlexColumnDiv>
          {/* collection 정보 end */}
        </FlexRowDiv>

        <HEIGHT size={80} />

        {/* 스키마 */}
        <Title title={hR.strings.AC_COLLECTION_READ_SCHEMA} />
        <HEIGHT size={15} />
        {sSchemaList.length === 0 ? (
          <>
            <HEIGHT size={15} />
            <CommonColumnBox
              style={{
                height: 250,
                alignSelf: 'stretch',
                justifyContent: 'center',
                alignItems: 'center',
                paddingTop: 40,
                paddingBottom: 40,
                ...devOutline,
              }}
            >
              <SAddBtn
                width={SCHEMA_CARD_LAYOUT.innerBoxWidth}
                height={SCHEMA_CARD_LAYOUT.innerBoxHeight}
                radius={SCHEMA_CARD_LAYOUT.innerBoxRadius}
                onClick={() => {
                  DBGMSG('new schema');
                  hHistory.push(Nav.brewSchemaCScreenPath({ cid: sFetchedData.col_info.cid }));
                }}
              />
              <HEIGHT size={24} />
              <TextCon
                size={14}
                color={R.colors.black}
                isBold
                text={`${hR.strings.AC_COLLECTION_READ_SCHEMA_DESC_1} ${hR.strings.AC_COLLECTION_READ_SCHEMA_DESC_2}`}
                containerStyle={{ marginBottom: 2 }}
              />
            </CommonColumnBox>
          </>
        ) : (
          renderList()
        )}

        <HEIGHT size={120} />
      </FlexColumnDiv>
    );
  } else {
    /**********************
     * Mobile render
     **********************/
    return (
      <>
        <MobileScalableFlexColumn
          getHeight={({ orgHeight, scaledHeight }) => {
            DBGMSG(`MobileScalableFlexColumn: orgHeight: ${orgHeight}`);
            DBGMSG(`MobileScalableFlexColumn: scaledHeight: ${scaledHeight}`);
          }}
        >
          <FlexColumnDiv
            style={{
              width: MOBILE_WIDTH,
              paddingLeft: MOBILE_HORIZONTAL_PADDING,
              paddingRight: MOBILE_HORIZONTAL_PADDING,
              paddingTop: CLAY.TITLE_PADDING_TOP,
              alignItems: 'flex-start',
            }}
          >
            {/* Back 버튼 */}
            <BackBtn />

            {/* 컬렉션 정보 */}
            {renderCollectionInfo()}

            <HEIGHT size={MY_COLLECTION_R_INFOBOX_MARGIN_TOP} />

            {/* Collection 카드 */}
            <CollectionCard collectionInfo={sFetchedData.col_info} />

            <HEIGHT size={12} />

            {/* 컬렉션 정보 상세 Box */}
            {renderCollectionInfoBox()}

            <HEIGHT size={12} />

            {/* 편집하기 버튼 */}
            {renderEditBtn()}

            <HEIGHT size={30} />
          </FlexColumnDiv>
        </MobileScalableFlexColumn>

        {/* 스키마 */}
        <MobileScalableFlexColumn
          getHeight={({ orgHeight, scaledHeight }) => {
            DBGMSG(`MobileScalableFlexColumn: orgHeight: ${orgHeight}`);
            DBGMSG(`MobileScalableFlexColumn: scaledHeight: ${scaledHeight}`);
          }}
        >
          <FlexColumnDiv
            style={{
              width: MOBILE_WIDTH,
              paddingLeft: MOBILE_HORIZONTAL_PADDING,
              paddingRight: MOBILE_HORIZONTAL_PADDING,
              alignItems: 'flex-start',
            }}
          >
            <Title title={hR.strings.AC_COLLECTION_READ_SCHEMA} />
            <HEIGHT size={20} />
          </FlexColumnDiv>
        </MobileScalableFlexColumn>

        {sSchemaList.length === 0 ? (
          <MobileScalableFlexColumn>
            <CommonColumnBox
              style={{
                width: 280,
                height: 241,
                alignSelf: 'stretch',
                justifyContent: 'center',
                alignItems: 'center',
                paddingTop: 34,
                paddingBottom: 34,
                marginLeft: CLAY.HPADDING,
                marginRight: CLAY.HPADDING,
              }}
              onClick={(e) => {
                DBGMSG('CommonColumnBox onClick');
                hHistory.push(Nav.brewSchemaCScreenPath({ cid: sFetchedData.col_info.cid }));
              }}
            >
              <SAddBtn
                width={SCHEMA_CARD_LAYOUT.innerBoxWidth}
                height={SCHEMA_CARD_LAYOUT.innerBoxHeight}
                radius={SCHEMA_CARD_LAYOUT.innerBoxRadius}
                onClick={() => {
                  DBGMSG('new schema');
                  hHistory.push(Nav.brewSchemaCScreenPath({ cid: sFetchedData.col_info.cid }));
                }}
                onMouseEnter={() => {
                  DBGMSG('onMouseEnter');
                }}
              />
              <HEIGHT size={24} />
              <TextConMulti
                size={10}
                color={R.colors.black}
                isBold
                text={[hR.strings.AC_COLLECTION_READ_SCHEMA_DESC_1, hR.strings.AC_COLLECTION_READ_SCHEMA_DESC_2]}
                containerStyle={{ marginBottom: 2, alignItems: 'center' }}
              />
            </CommonColumnBox>
          </MobileScalableFlexColumn>
        ) : (
          renderList()
        )}
        <MobileScalableFlexColumn>
          <HEIGHT size={60} />
          <CommonFooter />
        </MobileScalableFlexColumn>
      </>
    );
  }
}
